import React, { useState, useEffect, useMemo } from "react";
import Layout from "../components/Layout";

interface CoaTestResult {
  id: number;
  testType: string;
  pdfUrl: string;

  // Potency
  totalTHCPercent?: number;
  totalCBDPercent?: number;
  totalCannabinoidsPercent?: number;

  // Terpenes
  totalTerpenesPercent?: number;
  dominantTerpene1Name?: string;
  dominantTerpene1Percent?: number;

  // Pathogens
  pathogenOverallResult?: string;
  pathogensPass?: boolean;
}

interface CoaSample {
  id: number;
  sampleId: string;
  sampleName?: string;
  clientName?: string;
  tests: CoaTestResult[];
}

const TestResults: React.FC = () => {
  const [samples, setSamples] = useState<CoaSample[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const fetchSamples = async () => {
      try {
        setLoading(true);
        setErrorMsg(null);

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/coa/samples`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch COA samples");
        }

        const data: CoaSample[] = await response.json();
        setSamples(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching COA samples:", err);
        setErrorMsg("Unable to load test results right now.");
      } finally {
        setLoading(false);
      }
    };

    fetchSamples();
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter across sampleId, sampleName, clientName, and testType
  const filteredSamples = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return samples;

    return samples.filter((sample) => {
      const baseMatch =
        sample.sampleId.toLowerCase().includes(term) ||
        (sample.sampleName || "").toLowerCase().includes(term) ||
        (sample.clientName || "").toLowerCase().includes(term);

      if (baseMatch) return true;

      // Also allow matching on testType (POTENCY, TERPENES, PATHOGENS)
      return sample.tests.some((t) =>
        t.testType.toLowerCase().includes(term)
      );
    });
  }, [samples, searchTerm]);

  return (
    <Layout>
      <div className="bg-fractal-pattern-2 min-h-screen bg-cover bg-fixed p-8 pt-16 md:pt-24">
        <div className="container mx-auto p-6 bg-white bg-opacity-95 rounded-lg shadow-lg bg-weed-leaf bg-cover bg-center">
          <h1 className="text-4xl font-bold text-center mb-8 text-gray-900">
            Certificates of Analysis (C.O.A.)
          </h1>

          {/* Search */}
          <div className="mb-8 w-full max-w-lg mx-auto">
            <input
              type="text"
              placeholder="Search by sample ID, name, client, or test type..."
              className="border border-gray-300 p-4 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-green-500"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          {/* States: loading / error / empty / list */}
          {loading && (
            <p className="text-center text-gray-500">Loading test results…</p>
          )}

          {!loading && errorMsg && (
            <p className="text-center text-red-500">{errorMsg}</p>
          )}

          {!loading && !errorMsg && filteredSamples.length === 0 && (
            <p className="text-center text-gray-500">
              No test results found. Try a different search.
            </p>
          )}

          {!loading && !errorMsg && filteredSamples.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredSamples.map((sample) => (
                <div
                  key={sample.id}
                  className="bg-white p-6 rounded-lg shadow-lg border border-gray-100"
                >
                  <h2 className="font-semibold text-xl mb-1 text-gray-800">
                    {sample.sampleName || sample.sampleId}
                  </h2>
                  <p className="text-xs text-gray-500 mb-1">
                    Sample ID: {sample.sampleId}
                  </p>
                  <p className="text-xs text-gray-500 mb-4">
                    Client: {sample.clientName || "N/A"}
                  </p>

                  <div className="space-y-4">
                    {sample.tests.map((test) => {
                      // Use backend proxy so browser doesn't hit Nextcloud directly
                      const apiBase = process.env.REACT_APP_API_URL || "";
const viewerUrl = `${apiBase}/api/coa/file?url=${encodeURIComponent(
  test.pdfUrl
)}`;

                      return (
                        <div
                          key={test.id}
                          className="border-t pt-3 text-sm text-gray-800"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">
                              {test.testType}
                            </span>
                            <a
                              href={viewerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-600 hover:underline text-xs"
                            >
                              Open PDF
                            </a>
                          </div>

                          {/* POTENCY summary */}
                          {test.testType === "POTENCY" && (
                            <div className="text-xs space-y-1">
                              <p>
                                <strong>Total THC:</strong>{" "}
                                {test.totalTHCPercent != null
                                  ? `${test.totalTHCPercent.toFixed(2)}%`
                                  : "—"}
                              </p>
                              <p>
                                <strong>Total CBD:</strong>{" "}
                                {test.totalCBDPercent != null
                                  ? `${test.totalCBDPercent.toFixed(2)}%`
                                  : "—"}
                              </p>
                              <p>
                                <strong>Total Cannabinoids:</strong>{" "}
                                {test.totalCannabinoidsPercent != null
                                  ? `${test.totalCannabinoidsPercent.toFixed(
                                      2
                                    )}%`
                                  : "—"}
                              </p>
                            </div>
                          )}

                          {/* TERPENES summary */}
                          {test.testType === "TERPENES" && (
                            <div className="text-xs space-y-1">
                              <p>
                                <strong>Total Terpenes:</strong>{" "}
                                {test.totalTerpenesPercent != null
                                  ? `${test.totalTerpenesPercent.toFixed(2)}%`
                                  : "—"}
                              </p>
                              <p>
                                <strong>Dominant Terpene:</strong>{" "}
                                {test.dominantTerpene1Name || "N/A"}
                                {test.dominantTerpene1Percent != null
                                  ? ` (${test.dominantTerpene1Percent.toFixed(
                                      2
                                    )}%)`
                                  : ""}
                              </p>
                            </div>
                          )}

                          {/* PATHOGENS summary */}
                          {test.testType === "PATHOGENS" && (
                            <div className="text-xs space-y-1">
                              <p>
                                <strong>Overall:</strong>{" "}
                                {test.pathogenOverallResult || "—"}
                              </p>
                              <p>
                                <strong>Result:</strong>{" "}
                                {test.pathogensPass == null
                                  ? "—"
                                  : test.pathogensPass
                                  ? "PASS"
                                  : "FAIL"}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default TestResults;
